import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Notification from '../../notification/notification';
import { PlusIcon, ArrowUpTrayIcon } from '@heroicons/react/20/solid';
import { useSelector } from 'react-redux';
import menuApi from '../../../services/menuApi';
import { MenuUpsertModal } from './menu-upsert-modal';
import ModalDelete from '../../modal-delete/modal-delete';
import TWConfirmModal from '../../ui/tw-confirm-modal';
import MenuCard from './menu-card';
import MenuPublishModal from './menu-publish-modal';
import { MenuListSkeleton } from '../../skeleton/skeleton';
import { capitalizeWords, capitalizeSentence } from '../../../utils/baseUtils';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const GripIcon = ({ className }) => (
  <svg
    viewBox="0 0 20 20"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <circle cx="7.5" cy="5" r="1.25" />
    <circle cx="7.5" cy="10" r="1.25" />
    <circle cx="7.5" cy="15" r="1.25" />
    <circle cx="12.5" cy="5" r="1.25" />
    <circle cx="12.5" cy="10" r="1.25" />
    <circle cx="12.5" cy="15" r="1.25" />
  </svg>
);

function SortableMenuPill({ menu, isSelected, onSelect }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: menu.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      className={classNames(
        isSelected
          ? 'border-blue-500 bg-blue-50 text-blue-700 shadow-sm dark:border-sky-400 dark:bg-slate-700 dark:text-sky-300'
          : 'border-slate-300 bg-white text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700/70 dark:text-slate-300',
        isDragging ? 'shadow-md' : '',
        'group flex flex-shrink-0 items-center gap-1 rounded-full border pl-1.5 pr-3 py-1 text-xs font-semibold transition',
      )}
    >
      <button
        type="button"
        {...listeners}
        aria-label="Drag to reorder"
        className={classNames(
          isDragging ? 'cursor-grabbing' : 'cursor-grab',
          'flex h-5 w-4 items-center justify-center text-slate-300 opacity-60 transition hover:text-slate-500 group-hover:opacity-100 focus:outline-none dark:text-slate-500 dark:hover:text-slate-300',
        )}
      >
        <GripIcon className="h-3.5 w-3.5" />
      </button>
      <button
        type="button"
        onClick={onSelect}
        className="py-0.5 pr-1 text-xs font-semibold focus:outline-none"
      >
        {menu.name}
      </button>
    </div>
  );
}

export default function MenuList({
  selected,
  setSelected,
  preferredMenuId = null,
  onPreferredConsumed,
}) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const appliedPreferredRef = useRef(false);
  const [deletedItem, setDeletedItem] = useState({ name: '', id: '' });

  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    displayorder: false,
    stores: false,
  });
  const [menuForm, setMenuForm] = useState({
    id: '',
    name: '',
    description: '',
    displayorder: 0,
    stores: [],
    days: [],
  });
  const [menuDialogs, setMenuDialogs] = useState({
    menuUpsert: false,
    menuDelete: false,
  });
  const [menuList, setMenuList] = useState([]);
  const [loading, setLoading] = useState({
    menuLoading: true,
    categoryLoading: false,
    upsertLoading: false,
    deleteLoading: false,
  });

  const getMenuList = async (method) => {
    try {
      setLoading((prev) => ({ ...prev, menuLoading: true }));

      const query = `?companyid=${globalCompany.id}`;
      const list = await menuApi.list(query);

      setMenuList(list.data.data);
      if (list.data.data.length > 0) {
        const isUpdate = method?.method === 'update';
        // Deep-linked selection is one-shot: consume the ref on the first
        // non-'update' call regardless of match (same convention as
        // CategoryList/ProductList/Modifiers), so later reloads triggered by
        // delete/duplicate/create don't keep snapping selection back to the
        // originally deep-linked menu.
        let preferredIdToUse = null;
        if (!isUpdate && preferredMenuId && !appliedPreferredRef.current) {
          appliedPreferredRef.current = true;
          onPreferredConsumed?.('menuid');
          if (list.data.data.some((m) => m.id === preferredMenuId)) {
            preferredIdToUse = preferredMenuId;
          }
        }
        setSelected((prev) => ({
          ...prev,
          menuid: isUpdate
            ? prev?.menuid
            : (preferredIdToUse || list.data.data[0]?.id),
        }));
      } else {
        setSelected((prev) => ({
          ...prev,
          menuid: '',
          category: {},
          itemid: '',
          product: {},
          optiongroupid: '',
          optionid: '',
        }));
      }
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, menuLoading: false }));
    }
  };
  const onClickAddMenu = () => {
    setMenuForm({
      id: '',
      name: '',
      description: '',
      displayorder:
        menuList.length > 0
          ? menuList[menuList.length - 1].displayorder + 1
          : 1,
      stores: [],
      days: null,
    });

    setMenuDialogs((prev) => ({ ...prev, menuUpsert: true }));
  };

  const onFormChange = (val, key) => {
    setMenuForm((prev) => ({ ...prev, [key]: val }));
  };

  const onClickDeleteMenu = (menu) => {
    setDeletedItem(menu);
    setMenuDialogs((prev) => ({
      ...prev,
      menuUpsert: false,
      menuDelete: true,
    }));
  };

  const onConfirmDelete = async () => {
    try {
      setLoading((prev) => ({ ...prev, deleteLoading: true }));
      await menuApi.deleteById(deletedItem.id);
      getMenuList();
    } catch (error) {
    } finally {
      setMenuDialogs((prev) => ({
        ...prev,
        menuDelete: false,
        menuUpsert: false,
      }));
      setLoading((prev) => ({ ...prev, deleteLoading: false }));
    }
  };

  const [duplicateTarget, setDuplicateTarget] = useState(null);
  const [duplicateLoading, setDuplicateLoading] = useState(false);
  const [publishMenu, setPublishMenu] = useState(null);

  const onClickDuplicateMenu = (menu) => {
    setDuplicateTarget(menu);
  };

  const runDuplicateMenu = async () => {
    if (!duplicateTarget) return;
    setDuplicateLoading(true);
    try {
      const res = await menuApi.duplicate(duplicateTarget.id);
      const newName = res?.data?.data?.name || res?.data?.name || 'menu';
      setNotf({
        display: true,
        type: 'success',
        message: `Duplicated as "${newName}"`,
      });
      await getMenuList();
    } catch (e) {
      setNotf({
        display: true,
        type: 'error',
        message:
          e?.response?.data?.Message ||
          e?.response?.data ||
          'Duplicate failed',
      });
    } finally {
      setDuplicateLoading(false);
      setDuplicateTarget(null);
    }
  };

  const onClickEditMenu = (val) => {
    let stores = [];
    val.menustores.map((x) => stores.push(x.storeid));
    setMenuForm({
      id: val.id,
      name: val.name,
      description: val.description,
      displayorder: val.displayorder,
      stores,
      days: val.menudays,
    });

    setMenuDialogs((prev) => ({ ...prev, menuUpsert: true }));
  };

  const onCloseUpsert = () => {
    setErrors((prev) => ({
      ...prev,
      displayorder: false,
      name: false,
      stores: false,
    }));
    setMenuDialogs((prev) => ({ ...prev, menuUpsert: false }));
  };
  const onFormSubmit = async (days) => {
    if (!menuForm.name || menuForm.displayorder === '') {
      setErrors((prev) => ({
        ...prev,
        name: !menuForm.name ? true : false,
        displayorder: menuForm.displayorder === '' ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        name: false,
        displayorder: false,
        stores: false,
      }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = { ...menuForm, companyid: globalCompany?.id };
      // Capitalize menu name and description before saving
      if (body.name) {
        body.name = capitalizeWords(body.name.trim());
      }
      if (body.description) {
        body.description = capitalizeSentence(body.description.trim());
      }
      if (days) {
        let daysPayload = {
          days: days,
          menuid: menuForm.id,
        };
        await menuApi.upsertHours(daysPayload);
      }
      const res = await menuApi.upsert(body);
      setSelected((prev) => ({ ...prev, menuid: res.data.data.id }));
      if (res.data.data) {
        setMenuDialogs((prev) => ({ ...prev, menuUpsert: false }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getMenuList({ method: menuForm.id ? 'update' : 'create' });
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = menuList.findIndex((item) => item.id === active.id);
    const newIndex = menuList.findIndex((item) => item.id === over.id);
    const reordered = arrayMove(menuList, oldIndex, newIndex);

    setMenuList(reordered);

    try {
      await menuApi.reorder({
        items: reordered.map((item, index) => ({
          id: item.id,
          displayorder: index,
        })),
      });
    } catch (error) {
      console.error('Error reordering menus:', error);
      await getMenuList({ method: 'update' });
    }
  };

  useEffect(() => {
    getMenuList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalCompany.id]);

  return (
    <div className="flex w-full items-center overflow-hidden">
      {loading.menuLoading ? (
        <MenuListSkeleton />
      ) : (
        <>
          <div className="w-full overflow-x-auto hide-scrollbar lg:hidden">
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
              modifiers={[restrictToHorizontalAxis]}
            >
              <SortableContext
                items={menuList.map((m) => m.id)}
                strategy={horizontalListSortingStrategy}
              >
                <div className="flex min-w-max items-center gap-2 py-0.5">
                  {menuList.map((menu) => (
                    <SortableMenuPill
                      key={menu.id}
                      menu={menu}
                      isSelected={menu.id === selected.menuid}
                      onSelect={() => {
                        setSelected((prev) => ({
                          ...prev,
                          menuid: menu.id,
                          category: {},
                          product: {},
                          productid: '',
                          itemid: '',
                          optiongroupid: '',
                          optionid: '',
                        }));
                      }}
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => onClickAddMenu()}
                    className="flex flex-shrink-0 items-center gap-1 rounded-md border border-dashed border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-600 transition hover:bg-blue-50 dark:border-slate-500 dark:bg-slate-700/70 dark:text-slate-200"
                  >
                    <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Add
                  </button>
                  <Link
                    to="/dashboard/menus/import"
                    className="flex flex-shrink-0 items-center gap-1 rounded-md border border-dashed border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-600 transition hover:bg-emerald-50 dark:border-slate-500 dark:bg-slate-700/70 dark:text-emerald-300"
                  >
                    <ArrowUpTrayIcon className="h-3.5 w-3.5" aria-hidden="true" />
                    Import
                  </Link>
                </div>
              </SortableContext>
            </DndContext>
          </div>
          <div className="hidden w-full lg:block">
            <div className="w-full overflow-x-auto hide-scrollbar">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                modifiers={[restrictToHorizontalAxis]}
              >
                <SortableContext
                  items={menuList.map((m) => m.id)}
                  strategy={horizontalListSortingStrategy}
                >
                  <nav
                    className="flex min-w-max items-center gap-2 py-1 pr-1"
                    aria-label="Tabs"
                  >
                    {menuList.map((menu, i) => (
                      <MenuCard
                        key={menu.id}
                        index={i}
                        id={menu.id}
                        selected={selected}
                        setSelected={setSelected}
                        onClickEditMenu={onClickEditMenu}
                        onClickDuplicateMenu={onClickDuplicateMenu}
                        onClickPublishMenu={(m) => setPublishMenu(m)}
                        menu={menu}
                      />
                    ))}
                    <div
                      onClick={() => onClickAddMenu()}
                      className="flex min-h-[4.5rem] cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-blue-300 bg-white px-4 py-3 text-sm font-semibold text-blue-600 transition hover:bg-blue-50 dark:border-slate-500 dark:bg-slate-700/70 dark:text-slate-200 dark:hover:bg-slate-600"
                    >
                      <button
                        type="button"
                        className="rounded-full bg-blue-600 p-1 text-white shadow-sm"
                      >
                        <PlusIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                      <span className="truncate font-semibold text-[0.925rem]">
                        Add Menu
                      </span>
                    </div>
                    <Link
                      to="/dashboard/menus/import"
                      className="flex min-h-[4.5rem] cursor-pointer items-center justify-center gap-2 rounded-md border border-dashed border-emerald-300 bg-white px-4 py-3 text-sm font-semibold text-emerald-600 transition hover:bg-emerald-50 dark:border-slate-500 dark:bg-slate-700/70 dark:text-emerald-300 dark:hover:bg-slate-600"
                    >
                      <span className="rounded-full bg-emerald-600 p-1 text-white shadow-sm">
                        <ArrowUpTrayIcon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <span className="truncate font-semibold text-[0.925rem]">
                        Import Menu
                      </span>
                    </Link>
                  </nav>
                </SortableContext>
              </DndContext>
            </div>
          </div>
          <MenuUpsertModal
            show={menuDialogs.menuUpsert}
            loading={loading.upsertLoading}
            menuForm={menuForm}
            errors={errors}
            onCloseUpsert={onCloseUpsert}
            onFormChange={onFormChange}
            onFormSubmit={onFormSubmit}
            onClickDeleteMenu={onClickDeleteMenu}
          />

          <TWConfirmModal
            open={!!duplicateTarget}
            tone="info"
            title={`Duplicate "${duplicateTarget?.name || ''}"?`}
            description={
              duplicateTarget
                ? 'A copy will be created at the end of the menu list, along with categories, products and their items. Modifier group definitions are shared (not duplicated). This may take a few seconds.'
                : ''
            }
            confirmLabel="Duplicate"
            loading={duplicateLoading}
            onConfirm={runDuplicateMenu}
            onClose={() => !duplicateLoading && setDuplicateTarget(null)}
          />
          <MenuPublishModal
            show={!!publishMenu}
            menu={publishMenu}
            menuList={menuList}
            onClose={() => setPublishMenu(null)}
            onPublished={() => getMenuList({ method: 'update' })}
          />
          <ModalDelete
            title="Delete Menu"
            loading={loading.deleteLoading}
            open={menuDialogs.menuDelete}
            onCloseRequest={() =>
              setMenuDialogs((prev) => ({ ...prev, menuDelete: false }))
            }
            onOk={onConfirmDelete}
            item={deletedItem}
            shouldConfirm={true}
          />
          <Notification
            display={notf.display}
            type={notf.type}
            message={notf.message}
            onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
          />
        </>
      )}
    </div>
  );
}
